import React from "react";
import { cn } from "@/lib/utils";
import { MicButton, useMicButtonState } from "./MicButton";
import { useTranslation } from "@/lib/translate";
import { TypingIndicator } from "../chat/TypingIndicator";
import { TextInput } from "./TextInput";
import { InputModeToggle } from "./InputModeToggle";
import { useAppStore } from "@/hooks/useAppStore";
import { useIsMobile } from "@/hooks/useIsMobile";
import { RecordingOverlay } from "./RecordingOverlay";
import { LanguageSelector } from "./LanguageSelector";
import { VADRecordingIndicator } from "./VADRecordingIndicator";

export const InputArea: React.FC = () => {
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const isPresentationMode = useAppStore((state) => state.isPresentationMode);
  const inputMode = useAppStore((state) => state.inputMode);
  const t = useTranslation(preferredLanguage);
  const isTyping = useAppStore((state) => state.isTyping);
  const isMobile = useIsMobile();
  const isVADEnabled = useAppStore((state) => state.isVADEnabled);

  // Get recording state for overlay
  const { isRecording, waveBarRefs, timerLabel, handleToggleRecording, cancelRecording } = useMicButtonState();
  const toggleVAD = useAppStore((state) => state.toggleVAD);

  // Spacebar handler for recording
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key events from input fields or textareas
      const target = e.target as HTMLElement;
      const isTypingArea = ['INPUT', 'TEXTAREA'].includes(target.tagName);
      if (isTypingArea || target.isContentEditable) return;

      if (e.code === "Space") {
        e.preventDefault(); // Prevent page scroll

        // In presentation mode, spacebar toggles VAD instead of manual recording
        if (isPresentationMode) {
          toggleVAD();
        } else if (!isVADEnabled) {
          handleToggleRecording();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleToggleRecording, isPresentationMode, isVADEnabled, toggleVAD]);

  // if (isPresentationMode) return null; // Allow rendering in presentation mode

  return (
    <>
      {/* Presentation Mode Background Helper */}
      {isPresentationMode && (
        <div className="fixed inset-0 bg-black -z-10" />
      )}

      {/* Recording Overlay - rendered at top level */}
      <RecordingOverlay
        isRecording={isRecording}
        waveBarRefs={waveBarRefs}
        timerLabel={timerLabel}
        onStopRecording={handleToggleRecording}
        onCancelRecording={cancelRecording}
      />
      
      {/* VAD Recording Indicator - red border and mini wavelength */}
      <VADRecordingIndicator
        isVADEnabled={isVADEnabled}
        waveBarRefs={waveBarRefs}
        hideBorder={isPresentationMode}
      />
      {/* CSS Keyframes */}
      <style>{`
        @keyframes slideUpBounce {
          0% {
            transform: translateX(-50%) translateY(150px) scale(0.9);
            opacity: 0;
          }
          60% {
            transform: translateX(-50%) translateY(-10px) scale(1.02);
            opacity: 1;
          }
          80% {
            transform: translateX(-50%) translateY(5px) scale(0.98);
          }
          100% {
            transform: translateX(-50%) translateY(0) scale(1);
            opacity: 1;
          }
        }
      `}</style>
      
      {/* Typing Indicator */}
      <TypingIndicator isVisible={isTyping} />

      {/* Bottom Panel - Glassmorphism Design */}
      <div
        className="fixed bg-white/70 backdrop-blur-[30px] backdrop-saturate-[180%] px-6 py-3 flex flex-col items-center justify-center gap-1 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.5)_inset] pointer-events-auto rounded-[50px] border border-white/40 max-w-[600px] w-[90%] opacity-0 transition-all duration-400 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
        style={{
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 5,
          animation: 'slideUpBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
        }}
      >
        {/* Controls Container */}
        <div
          className="flex items-center justify-between w-full relative"
        >
          {/* Left: Keyboard Toggle + VAD Toggle */}
          {!isPresentationMode ? (
            <div className="flex items-center gap-2">
              <InputModeToggle />
            </div>
          ) : (
            <div /> // Placeholder to preserve justify-between layout
          )}

          {/* Center: Mic Button or Text Input */}
          <div
            className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center"
          >
            {inputMode === "mic" || isPresentationMode ? (
              <MicButton 
                onToggleRecording={isPresentationMode ? toggleVAD : handleToggleRecording} 
                isRecording={isRecording}
                waveBarRefs={waveBarRefs}
              />
            ) : (
              <div className="w-40">
                <TextInput />
              </div>
            )}
          </div>

          {/* Right: Language Selector - Always use CompactLanguageDropdown with flags */}
          <div className="flex items-center">
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      {!isMobile && (
        <div
          className={cn(
            "fixed bottom-[92px] left-1/2 -translate-x-1/2 text-center text-xs z-[4]",
            isPresentationMode ? "text-white/50" : "text-black/50"
          )}
        >
          {t("⚠ AI can make mistakes!")}
        </div>
      )}
    </>
  );
};
